class RecipeContent extends HTMLElement {
  constructor() {
    super();

    this.servingsInput = this.querySelector('.recipe-servings__input');
    this.servingsValue = this.querySelector('.recipe-detail__value--servings');

    this.addEventListener('change', this.onChange.bind(this));
  }

  onChange(event) {
    if(event.target.name === 'RecipeServings') {
      this.onServingsChange(event);
    }
    else if(event.target.name === 'RecipeMeasurementUnit') {
      this.onMeasurementUnitChange(event);
    }
  }

  onMeasurementUnitChange(event) {
    const measurementUnit = event.target.value;

    const servings = parseInt(this.servingsInput.value);
    
    this.updateServings(servings);
    this.updateMeasurementUnit(measurementUnit);    
  }

  onServingsChange(event) {
    const servings = parseInt(event.target.value);
    this.servingsValue.textContent = servings;

    const measurementUnit = this.querySelector('input[name="RecipeMeasurementUnit"]:checked').value;

    this.updateServings(servings);
    this.updateMeasurementUnit(measurementUnit);
  }

  updateMeasurementUnit(measurementUnit) {
    const FL_OZ_TO_ML = 29.5735;
    const ingredients = this.querySelectorAll('.recipe-table__item-value');

    ingredients.forEach(ingredient => {
      const currentValue = ingredient.textContent.trim();
      const hasFlOz = /fl\s*oz/i.test(currentValue);
      const hasMl = /\bml\b/i.test(currentValue) && !hasFlOz;

      // Selected ml → convert any "fl oz" amounts.
      if (measurementUnit === 'ml' && hasFlOz) {
        ingredient.textContent = currentValue.replace(
          /(\d+(?:\.\d+)?)\s*fl\s*oz/gi,
          (_, num) => `${Math.round(parseFloat(num) * FL_OZ_TO_ML)} ml`
        );
        return;
      }

      // Selected oz → convert any "ml" amounts.
      if (measurementUnit === 'oz' && hasMl) {
        ingredient.textContent = currentValue.replace(
          /(\d+(?:\.\d+)?)\s*ml/gi,
          (_, num) => {
            const oz = parseFloat(num) / FL_OZ_TO_ML;
            const rounded = Math.round(oz * 4) / 4; // nearest 0.25
            return `${rounded} fl oz`;
          }
        );
      }
    });
  }

  updateServings(servings) {
    const ingredients = this.querySelectorAll('.recipe-table__item-value');

    ingredients.forEach(ingredient => {
      const original = ingredient.dataset.originalValue;

      if (servings === 1) {
        ingredient.textContent = original;
        return;
      }

      ingredient.textContent = original.replace(/\d+(\.\d+)?/g, match => {
        const scaled = parseFloat(match) * servings;
        return Number.isInteger(scaled) ? scaled.toString() : scaled.toFixed(2).replace(/\.?0+$/, '');
      });
    });
  }
}

if (!customElements.get('recipe-content')) {
  customElements.define('recipe-content', RecipeContent);
}

class RecipeShare extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.copyButton = this.querySelector('[data-share-copy]');
    this.copyLabel = this.querySelector('[data-copy-label]');
    this.popupLinks = this.querySelectorAll('[data-share-popup]');
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.copyButton) {
      this.copyButton.addEventListener('click', this.onCopy.bind(this));
    }

    this.popupLinks.forEach(link => {
      link.addEventListener('click', this.onPopupOpen.bind(this));
    });

    // Web Share API: prefer the native share sheet when supported.
    // Otherwise let <details> open the dropdown as a fallback.
    if (this.summary && navigator.share) {
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }
  }

  async onSummaryClick(event) {
    // Only intercept when opening — let clicks-to-close behave normally.
    if (this.details.open) return;

    event.preventDefault();
    try {
      await navigator.share({
        title: document.title,
        url: this.shareUrl,
      });
    } catch (e) {
      // User cancelled or share failed — fall back to opening the dropdown.
      if (e && e.name !== 'AbortError') {
        this.details.open = true;
      }
    }
  }

  get shareUrl() {
    return this.copyButton ? this.copyButton.dataset.shareUrl : window.location.href;
  }

  onDocumentClick(event) {
    if (this.details.open && !this.contains(event.target)) {
      this.details.open = false;
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.details.open) {
      this.details.open = false;
      this.details.querySelector('summary').focus();
    }
  }

  async onCopy() {
    try {
      await navigator.clipboard.writeText(this.shareUrl);
      const original = this.copyLabel.textContent;
      this.copyLabel.textContent = 'Copied!';
      setTimeout(() => { this.copyLabel.textContent = original; }, 1500);
    } catch (e) {
      // Fallback: select-and-prompt
      window.prompt('Copy this link:', this.shareUrl);
    }
  }

  onPopupOpen(event) {
    // Open social share URLs in a centered popup window instead of a new tab.
    const href = event.currentTarget.href;
    const width = 575;
    const height = 400;
    const left = (window.outerWidth - width) / 2 + (window.screenX || 0);
    const top = (window.outerHeight - height) / 2 + (window.screenY || 0);
    const opts = `status=1,width=${width},height=${height},top=${top},left=${left}`;

    const popup = window.open(href, 'share', opts);
    if (popup) {
      event.preventDefault();
      this.details.open = false;
    }
    // If popup was blocked, let the default target="_blank" handle it.
  }
}

if (!customElements.get('recipe-share')) {
  customElements.define('recipe-share', RecipeShare);
}